import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@monorepo/shared-auth';
import Button from '../../components/common/Button/Button';

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, signup, loginWithGoogle, loginWithApple, getUserProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get returnTo URL and message from navigation state
  const returnTo = location.state?.returnTo || '/';
  const customMessage = location.state?.message;

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let userCredential;

      if (isLogin) {
        userCredential = await login(email, password);
        const profile = await getUserProfile(userCredential.user.uid);

        if (!profile) {
          navigate('/profile-setup', { state: { returnTo } });
        } else {
          navigate(returnTo);
        }
      } else {
        userCredential = await signup(email, password);
        navigate('/profile-setup', { state: { returnTo } });
      }
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists. Please sign in instead.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password should be at least 6 characters.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        setError('Invalid email or password.');
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSocialAuth = async (provider) => {
    setError('');
    setLoading(true);

    try {
      let userCredential;

      if (provider === 'google') {
        userCredential = await loginWithGoogle();
      } else if (provider === 'apple') {
        userCredential = await loginWithApple();
      }

      const profile = await getUserProfile(userCredential.user.uid);

      if (!profile) {
        navigate('/profile-setup', { state: { returnTo } });
      } else {
        navigate(returnTo);
      }
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign in was cancelled.');
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        setError('An account already exists with this email using a different sign-in method.');
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="ub-auth-page">
      <div className="ub-auth-container">
        <div className="ub-auth-card">
          <h1 className="ub-auth-title">
            {isLogin ? 'Welcome Back!' : 'Join UBsports'}
          </h1>
          <p className="ub-auth-subtitle">
            {customMessage
              ? customMessage
              : isLogin
                ? 'Sign in to access your leagues and profile'
                : 'Create an account to unlock all features'}
          </p>

          {error && <div className="ub-auth-error">{error}</div>}

          <div className="ub-social-auth">
            <button
              onClick={() => handleSocialAuth('google')}
              disabled={loading}
              className="ub-social-btn ub-google-btn"
            >
              <span className="ub-social-icon">G</span>
              Continue with Google
            </button>

            <button
              onClick={() => handleSocialAuth('apple')}
              disabled={loading}
              className="ub-social-btn ub-apple-btn"
            >
              <span className="ub-social-icon"></span>
              Continue with Apple
            </button>
          </div>

          <div className="ub-auth-divider">
            <span>or</span>
          </div>

          <form onSubmit={handleEmailAuth} className="ub-auth-form">
            <div className="ub-form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your@email.com"
              />
            </div>

            <div className="ub-form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                minLength={6}
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              size="large"
              disabled={loading}
              className="ub-auth-submit ub-button-style-one"
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            </Button>
          </form>

          <div className="ub-auth-toggle">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
              }}
              className="ub-toggle-link"
            >
              {isLogin ? 'Sign up' : 'Sign in'}
            </button>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Auth;
